import { useQuery } from "@tanstack/react-query";
import type { AdminCareRequest, AdminCoopStatus, CoopPerson } from "@mm/shared";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "@/lib/api";
import { ago, when } from "@/lib/dates";
import { Badge, Card, cx, Empty, ErrorText, Input } from "@/ui/kit";
import { PageHeader } from "@/ui/Shell";

const TABS: { key: AdminCoopStatus; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "matched", label: "Matched" },
  { key: "past", label: "Past" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];
const STATUS_TONE = { open: "butter", matched: "sage", completed: "mist", cancelled: "blush", expired: "mist" } as const;
const OFFER_TONE = { pending: "butter", accepted: "sage", declined: "mist", superseded: "mist", withdrawn: "blush" } as const;
const WHERE = { mine: "At the requester's home", sitter: "At the sitter's home", either: "Either home" } as const;

interface MemberRow { id: string; firstName: string | null; lastName: string | null; email: string; neighborhoodName: string | null }
const nameOf = (p: { firstName: string | null; lastName: string | null }) => [p.firstName, p.lastName].filter(Boolean).join(" ") || "(no name)";

export default function Coop() {
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as AdminCoopStatus | null) ?? "open";
  const userId = params.get("user");
  const userName = params.get("name");
  const update = (next: Record<string, string | null>) =>
    setParams((p) => {
      const n = new URLSearchParams(p);
      for (const [k, v] of Object.entries(next)) (v ? n.set(k, v) : n.delete(k));
      return n;
    }, { replace: true });
  const filterBy = (p: { id: string; firstName: string | null; lastName: string | null }) => update({ user: p.id, name: nameOf(p) });

  const q = useQuery({
    queryKey: ["admin-coop", status, userId],
    queryFn: () => api<AdminCareRequest[]>(`/v1/admin/coop/requests?status=${status}${userId ? `&userId=${userId}` : ""}`),
  });

  return (
    <>
      <PageHeader title="Co-op" subtitle="Every sitting request, with the moms' notes." />
      <div className="mb-3 flex overflow-x-auto rounded-full bg-mist p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={status === t.key} onClick={() => update({ status: t.key })} className={cx("min-h-10 flex-1 whitespace-nowrap rounded-full px-3 text-sm", status === t.key ? "bg-white font-bold shadow-sm" : "")}>
            {t.label}
          </button>
        ))}
      </div>
      {userId ? (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-muted">Showing requests involving</span>
          <button type="button" onClick={() => update({ user: null, name: null })} className="rounded-full bg-ink px-3 py-1 text-sm font-semibold text-white" aria-label={`Clear filter ${userName ?? ""}`}>
            {userName ?? "this mom"} ✕
          </button>
        </div>
      ) : (
        <MomFilter onPick={filterBy} />
      )}

      <ErrorText error={q.error} />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
        {(q.data ?? []).map((r) => (
          <RequestCard key={r.id} r={r} onPick={filterBy} />
        ))}
      </div>
      {q.isSuccess && !q.data.length ? (
        <Empty title={`No ${status === "all" ? "" : TABS.find((t) => t.key === status)!.label.toLowerCase() + " "}requests`}>{userId ? `${userName ?? "This mom"} has none here — try another tab.` : "Requests show up here as moms post them."}</Empty>
      ) : null}
    </>
  );
}

/** Type a name or email, pick a mom: the list narrows to requests she made or offered on. */
function MomFilter({ onPick }: { onPick(p: MemberRow): void }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const r = useQuery({ queryKey: ["members", dq], queryFn: () => api<MemberRow[]>(`/v1/admin/members?q=${encodeURIComponent(dq)}`), enabled: dq.length > 0 });
  return (
    <div className="relative mb-4">
      <Input placeholder="Filter by mom (name or email)" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter by mom" />
      {dq && r.data ? (
        <ul className="absolute inset-x-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-2xl border border-line bg-white p-1 shadow-lg" role="listbox">
          {r.data.length ? (
            r.data.slice(0, 12).map((m) => (
              <li key={m.id}>
                <button type="button" role="option" aria-selected={false} onClick={() => { onPick(m); setQ(""); }} className="block w-full min-w-0 rounded-xl px-3 py-2 text-left hover:bg-mist">
                  <span className="block font-semibold">{nameOf(m)}</span>
                  <span className="block truncate text-sm text-muted">{[m.neighborhoodName, m.email].filter(Boolean).join(" · ")}</span>
                </button>
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-sm text-muted">No one matches “{dq}”.</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}

function Mom({ p, onPick }: { p: CoopPerson; onPick(p: CoopPerson): void }) {
  return (
    <button type="button" onClick={() => onPick(p)} className="font-semibold underline decoration-line underline-offset-2 hover:decoration-ink" title={`Show only requests involving ${nameOf(p)}`}>
      {nameOf(p)}
    </button>
  );
}

function RequestCard({ r, onPick }: { r: AdminCareRequest; onPick(p: CoopPerson): void }) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{r.title}</p>
          <p className="text-sm text-muted">
            {when(r.startsAt)} – {new Date(r.endsAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} · {r.hours} h · {WHERE[r.locationMode]}
          </p>
        </div>
        <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>
      </div>

      <p className="mt-2 text-sm">
        Requested by <Mom p={r.requester} onPick={onPick} />
        {r.requester.neighborhoodName ? <span className="text-muted"> · {r.requester.neighborhoodName}</span> : null}
        <span className="text-muted"> · posted {ago(r.createdAt)}</span>
      </p>
      {r.kids.length ? <p className="text-sm text-muted">Kids: {r.kids.map((k) => `${k.firstName} (${k.age})`).join(", ")}</p> : null}

      {r.details || r.requesterNotes ? (
        <div className="mt-3 grid gap-2">
          {r.details ? <Note label="Request details" text={r.details} /> : null}
          {r.requesterNotes ? <Note label="Her standing notes for sitters" text={r.requesterNotes} /> : null}
        </div>
      ) : null}

      {r.matchedSitter ? (
        <p className="mt-3 text-sm">
          <Badge tone="sage">Sitter</Badge> <Mom p={r.matchedSitter} onPick={onPick} />
          {r.session ? (
            <span className="text-muted">
              {" "}· {r.session.confirmedHours !== null ? `${r.session.confirmedHours} h confirmed${r.session.finalizedBy ? ` (${r.session.finalizedBy})` : ""}` : `${r.session.scheduledHours} h scheduled`}
            </span>
          ) : null}
        </p>
      ) : null}

      <div className="mt-3 border-t border-line pt-3">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">
          {r.offers.length ? `${r.offers.length} ${r.offers.length === 1 ? "offer" : "offers"}` : r.status === "open" ? `No offers yet · ${r.notifiedCount} matching ${r.notifiedCount === 1 ? "mom" : "moms"} notified` : "No offers"}
        </p>
        <ul className="grid gap-2">
          {r.offers.map((o) => (
            <li key={o.id} className="rounded-xl bg-cream p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Mom p={o.sitter} onPick={onPick} />
                <Badge tone={OFFER_TONE[o.status]}>{o.status}</Badge>
                <span className="text-xs text-muted">{ago(o.createdAt)}</span>
              </div>
              {o.note ? <p className="mt-1 whitespace-pre-wrap break-words text-sm">“{o.note}”</p> : <p className="mt-1 text-sm text-muted">No note</p>}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

function Note({ label, text }: { label: string; text: string }) {
  return (
    <div className="rounded-xl bg-butter/60 p-3">
      <p className="text-xs font-bold uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{text}</p>
    </div>
  );
}
